/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MerchantService, NotificationService, ReportsService } from "../../../../services";
import { faEraser } from "@fortawesome/free-solid-svg-icons";
import { PATTERN_CONSTANTS } from "../../../../utils/data.rest.constants";
import { Chargeback, Merchant } from "../../../../models";
import { ToastrService } from "ngx-toastr";
import { BsModalRef, BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { CollectChargebackModalComponent } from "../collect-chargeback-modal/collect-chargeback-modal.component";

@Component({
  selector: "app-collect-chargebacks",
  templateUrl: "./collect-chargebacks.component.html"
})
export class CollectChargebacksComponent implements OnInit {
  chargeBackFormGroup!: FormGroup;
  fee!: number;
  failedGlobalConfig = true;
  total = 0;
  buttonName = "Collect Chargeback";
  actionInProgress = false;
  faEraser = faEraser;
  @Input() merchantId!: number;
  @Input("merchant") merchant!: Merchant;
  @Output() collectChargeBackEvent = new EventEmitter<Chargeback>();
  @Output() cancelChargeBackEvent = new EventEmitter<boolean>();
  constructor(
    private fb: FormBuilder,
    private reportService: ReportsService,
    private notificationService: NotificationService,
    private merchantService: MerchantService,
    private toast: ToastrService,
    public modalRef: BsModalRef,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.reportService.getChargebackFee().subscribe(
      (report) => {
        this.fee = Number(report.CHARGEBACK_FEE) / 100;
        this.failedGlobalConfig = false;
      },
      (error) => {
        this.notificationService.error(error.error);
      }
    );
    this.chargeBackFormGroup = this.fb.group({
      transactionId: ["", [Validators.required, Validators.minLength(8), Validators.pattern(PATTERN_CONSTANTS.NUMBER)]],
      orderId: ["", [Validators.required]],
      amount: ["", [Validators.required, Validators.pattern(PATTERN_CONSTANTS.MONEY), Validators.min(0.01)]]
    });
  }

  calculateTotal(event: any): void {
    this.total = Math.round((+this.fee + +event.target.value) * 100) / 100;
  }

  receiveEditEvent(value: boolean) {
    this.cancelChargeBackEvent.emit(value);
  }

  clearValues() {
    this.chargeBackFormGroup.reset();
    this.total = this.fee;
  }

  openConfirmDialog() {
    if (this.chargeBackFormGroup.valid) {
      const modalDetials = {
        merchant: this.merchant,
        details: {
          parentExternalTransactionId: this.chargeBackFormGroup.controls.transactionId.value,
          orderId: this.chargeBackFormGroup.controls.orderId.value,
          chargebackAmount: this.chargeBackFormGroup.controls.amount.value,
          chargebackFee: this.fee,
          chargebackTotal: this.total
        }
      };
      const collectChargeBackModalOptions: ModalOptions = {
        initialState: {
          chargeback: modalDetials
        },
        ignoreBackdropClick: false,
        keyboard: false
      };
      this.modalRef = this.modalService.show(CollectChargebackModalComponent, collectChargeBackModalOptions);
      this.modalRef.content.onClose.subscribe((result: any) => {
        if (result) {
          this.collectChargeback();
        }
      });
    } else {
      this.chargeBackFormGroup.markAllAsTouched();
    }
  }

  collectChargeback() {
    if (this.chargeBackFormGroup.valid) {
      this.actionInProgress = true;
      const tempChargeback: Chargeback = {
        parentExternalTransactionId: this.chargeBackFormGroup.controls.transactionId.value,
        parentOrderId: this.chargeBackFormGroup.controls.orderId.value,
        chargebackAmount: this.chargeBackFormGroup.controls.amount.value * 100,
        chargebackDate: ""
      };
      this.merchantService.collectChargeBack(this.merchantId, tempChargeback).subscribe(
        (chargeback) => {
          chargeback.chargebackDate = new Date().toDateString();
          this.collectChargeBackEvent.emit(chargeback);
          this.toast.success(
            "You have successfully collected a $" +
              this.chargeBackFormGroup.controls.amount.value +
              " chargeback from merchant ID " +
              this.merchantId +
              " with a $" +
              this.fee +
              " chargeback fee ($" +
              this.total +
              " total).",
            "Chargeback Successful"
          );
          this.actionInProgress = false;
        },
        (error) => {
          const transactionError = error.error.message ? error.error.message : "Unknown Error Occurred";
          this.toast.error(
            "The chargeback cannot be collected at this time. Please try again later. Error: " + transactionError,
            "Chargeback Request Error"
          );
          this.actionInProgress = false;
        }
      );
    } else {
      this.chargeBackFormGroup.markAllAsTouched();
    }
  }
}
